/**
 * Article content processor for splitting body text on markers
 * and adding bylines/captions to inline images.
 */

/**
 * Split article body text on {{factbox.ID}} and {{poll}} markers.
 * Returns an array of segments for rendering.
 *
 * @param {string} body - The article body markdown text
 * @returns {Array<{type: string, content?: string, articleId?: number}>}
 */
export function splitBodySegments(body) {
    if (!body) return [{ type: 'markdown', content: '' }];

    const segments = [];
    // Match {{factbox.123}}, {{lesogsa.123}}, {{product.123}}, {{poll}}, {{map}}, {{video.0}},
    // or {{paywall}} (the paywall marker is stripped here; it is only meaningful server-side).
    const markerRegex = /\{\{(factbox\.(\d+)|lesogsa\.(\d+)|video\.(\d+)|results\.(\d+)|product\.(\d+)|events(?:\.([^{}]+))?|poll|map|paywall)\}\}/g;
    let lastIndex = 0;
    let match;

    while ((match = markerRegex.exec(body)) !== null) {
        // Add markdown segment before this marker
        if (match.index > lastIndex) {
            const content = body.substring(lastIndex, match.index);
            if (content.trim()) {
                segments.push({ type: 'markdown', content });
            }
        }

        if (match[1] === 'poll') {
            segments.push({ type: 'poll' });
        } else if (match[1] === 'map') {
            segments.push({ type: 'map' });
        } else if (match[1] && match[1].startsWith('events')) {
            const raw = (match[7] || '').trim();
            const calendarIds = [];
            const searchTokens = [];
            if (raw.length > 0) {
                const tokens = raw.split(/\s+/);
                tokens.forEach((tok, i) => {
                    if (i === 0 && !tok.startsWith('-')) {
                        tok.split(',').map(s => s.trim()).filter(s => s.length > 0).forEach(s => calendarIds.push(s));
                    } else if (tok.length > 0) {
                        searchTokens.push(tok);
                    }
                });
            }
            segments.push({ type: 'events', calendarIds, searchText: searchTokens.join(' ') });
        } else if (match[2]) {
            segments.push({ type: 'factbox', articleId: parseInt(match[2], 10) });
        } else if (match[3]) {
            segments.push({ type: 'lesogsa', articleId: parseInt(match[3], 10) });
        } else if (match[4] !== undefined) {
            segments.push({ type: 'video', videoIndex: parseInt(match[4], 10) });
        } else if (match[5] !== undefined) {
            segments.push({ type: 'results', resultsIndex: parseInt(match[5], 10) });
        } else if (match[6] !== undefined) {
            segments.push({ type: 'product', productId: parseInt(match[6], 10) });
        }

        lastIndex = match.index + match[0].length;
    }

    // Add remaining markdown after last marker
    const remaining = body.substring(lastIndex);
    if (remaining.trim()) {
        segments.push({ type: 'markdown', content: remaining });
    }

    // If no markers found, return the whole body as a single segment
    if (segments.length === 0) {
        segments.push({ type: 'markdown', content: body });
    }

    return segments;
}

/**
 * Fix unquoted src attributes in <img> tags produced by replaceImages().
 * replaceImages outputs: <img src=URL class='...' style='...' />
 * Markdown parsers need: <img src="URL" class='...' style='...' />
 *
 * @param {string} html - The HTML string after replaceImages() processing
 * @returns {string} - HTML with properly quoted src attributes
 */
export function fixImageSrc(html) {
    if (!html) return html;
    // Quote unquoted src, make images full-width and remove float
    return html.replace(/<img src=([^\s"'>]+)([^>]*)\/?>/g, (match, src, rest) => {
        return `<img src="${src}" class="img-fluid w-100 rounded my-2" style="width: 100%;" />`;
    });
}

/**
 * After replaceImages() has processed the markdown, scan the resulting HTML
 * for <img> tags that match article images and wrap them in <figure> with
 * <figcaption> showing caption and photographer credit.
 *
 * @param {string} html - The HTML string after replaceImages() processing
 * @param {Array} images - The article.images array
 * @returns {string} - HTML with images wrapped in figures with captions
 */
export function addBylinesToImages(html, images) {
    if (!html || !images || !images.length) return html;

    // Build a lookup from image URLs to their metadata
    const imageMap = {};
    images.forEach(img => {
        if (!img) return;
        // Map all possible image URL variants to this image object
        const urls = [
            img.s3XXLargeLink,
            img.s3XLargeLink,
            img.s3LargeLink,
            img.s3MediumLink,
            img.s3SmallLink,
            img.s3Link,
        ].filter(Boolean);
        urls.forEach(url => {
            imageMap[url] = img;
        });
    });

    // Find <img> tags and wrap ones that have caption/photographer data
    // Handles both quoted src="url" and unquoted src=url formats (replaceImages outputs unquoted)
    return html.replace(/<img\s+[^>]*src=(?:"([^"]*)"|'([^']*)'|([^\s/>]+))[^>]*\/?>/g, (imgTag, srcQuoted, srcSingle, srcUnquoted) => {
        const src = srcQuoted || srcSingle || srcUnquoted;
        const img = imageMap[src];
        if (!img || (!img.caption && !img.photographer)) {
            return imgTag;
        }

        const captionParts = [];
        if (img.caption) {
            captionParts.push(img.caption);
        }
        if (img.photographer) {
            captionParts.push(`<span class="fst-italic">Foto: ${img.photographer}</span>`);
        }
        const figcaption = captionParts.join(' ');

        return `<figure class="article-inline-image my-3">${imgTag}<figcaption class="text-body-secondary small mt-1">${figcaption}</figcaption></figure>`;
    });
}
